import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import { caListingDataAtom, useCAListFilterValue } from './state';

const CAInsights: React.FC = () => {
  const listingData = useAtomValue(caListingDataAtom);
  const insights = listingData?.insights;
  const selectedCloudAgentsFilter = useCAListFilterValue<
    'active' | 'aging' | 'deleted'
  >('filter');
  if (!listingData || !insights || Object.keys(insights).length === 0) {
    return <div>Loading...</div>;
  }

  // Extract stats from insights
  const stats = {
    active: insights.status_breakdown.active,
    aging: insights.status_breakdown.aging,
    deleted: insights.status_breakdown.deleted,
    servers_count: insights.servers_count,
    workstations_count: insights.workstations_count,
    total_vulnerabilities: insights.total_vulnerabilities,
    unique_os_count: insights.unique_os_count,
    urgent: insights.severity_breakdown.urgent,
    critical: insights.severity_breakdown.critical,
    serious: insights.severity_breakdown.serious,
    medium: insights.severity_breakdown.medium,
    minimal: insights.severity_breakdown.minimal,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Severity */}
          {selectedCloudAgentsFilter !== 'deleted' && (
            <div
              className="mr-2 pr-2 flex-grow-1 border-right"
              style={{ borderColor: '#dee2e6' }}
            >
              <h6 className="mb-0">Severity:</h6>
              <SegmentedBar
                height="5px"
                width="100%"
                segments={[
                  {
                    color: 'urgent',
                    value: stats.urgent,
                    label: 'Urgent',
                  },
                  {
                    color: 'critical',
                    value: stats.critical,
                    label: 'Critical',
                  },
                  {
                    color: 'serious',
                    value: stats.serious,
                    label: 'Serious',
                  },
                  {
                    color: 'medium',
                    value: stats.medium,
                    label: 'Medium',
                  },
                  {
                    color: 'minimal',
                    value: stats.minimal,
                    label: 'Minimal',
                  },
                ]}
                labelDisplay="value"
                tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
              />
            </div>
          )}
          {/* Count Summaries */}
          {selectedCloudAgentsFilter !== 'deleted' && (
            <InsightItem
              label="Active"
              value={stats.active}
              tooltip="Active Cloud Agents, including those nearing aging out."
            />
          )}
          {selectedCloudAgentsFilter !== 'deleted' && stats.aging > 0 && (
            <InsightItem
              label="Aging Out"
              value={stats.aging}
              tooltip="Active Cloud Agents nearing aging out."
            />
          )}
          {selectedCloudAgentsFilter === 'deleted' && (
            <InsightItem
              label="Deleted"
              value={stats.deleted}
              tooltip="Deleted Cloud Agents (aged out or manually deleted)."
            />
          )}
          <InsightItem label="Servers" value={stats.servers_count} />
          <InsightItem label="Workstations" value={stats.workstations_count} />
          {selectedCloudAgentsFilter !== 'deleted' && (
            <InsightItem
              label="Vulnerabilities"
              value={stats.total_vulnerabilities}
              tooltip="Total number of vulnerabilities"
            />
          )}
          <InsightItem
            label="Operating Systems"
            value={stats.unique_os_count}
            isLast
          />
        </div>
      </Card>
    </div>
  );
};

export default CAInsights;
